import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DateTime } from 'luxon';
import { ManifestPodPreview } from 'src/app/features/model/hawb.model';
import { HawbStatus, ModalPopupMessages, popupButtons } from './constants';
import { getAdditionalDetails } from './string.util';

export const POD_STATUS_CODE = 'POD';
export const POD_DRIVER_NAME_PATTERN = /^[a-zA-Z ]*$/;
export const POD_NAME_MAX_LENGTH = 50;
/** Status Codes tab POD details save through `statusCodesPayload`, which allows 126. */
export const STATUS_CODE_POD_NAME_MAX_LENGTH = 126;

/** FR-BPM-UI-011: nothing eligible and every undelivered master is held. */
export function isManifestPodAllHeld(
  preview: ManifestPodPreview | null | undefined
): boolean {
  return (
    !!preview &&
    preview.eligible.length === 0 &&
    preview.heldCount > 0 &&
    preview.blockedCount === 0
  );
}

export function isManifestPodNoneEligible(
  preview: ManifestPodPreview | null | undefined
): boolean {
  return (
    !!preview &&
    preview.eligible.length === 0 &&
    (preview.heldCount > 0 || preview.blockedCount > 0)
  );
}

/** FR-BPM-UI-008 / 011 — `null` means no banner (§11 Q3). */
export function getManifestPodBannerMessage(
  preview: ManifestPodPreview | null | undefined
): string | null {
  if (!preview) {
    return null;
  }
  if (isManifestPodAllHeld(preview)) {
    return ModalPopupMessages.manifestPodAllHeldBanner;
  }
  if (!preview.eligible.length) {
    return null;
  }
  // Blocked shipments are left out of `eligible` too, so the banner counts them as held.
  const notListedCount = preview.heldCount + preview.blockedCount;
  return notListedCount > 0
    ? ModalPopupMessages.manifestPodHeldShipments(notListedCount)
    : ModalPopupMessages.manifestPodNoHeldShipments;
}

export function getManifestPodLinkedShipmentsLabel(count: number): string {
  return ModalPopupMessages.manifestPodViewLinkedShipments(count);
}

export function podNameTrimmedLengthValidator(
  maxLength: number = POD_NAME_MAX_LENGTH
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString();
    if (!raw.length) {
      return null;
    }
    const trimmed = raw.trim();
    if (!trimmed.length) {
      return { minlength: { requiredLength: 1, actualLength: 0 } };
    }
    if (trimmed.length > maxLength) {
      return {
        maxlength: {
          requiredLength: maxLength,
          actualLength: trimmed.length,
        },
      };
    }
    return null;
  };
}

export function podCarrierRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null;
    }
    const carrier = (control.get('carrierName')?.value ?? '').toString().trim();
    return carrier ? null : { podNameRequired: true };
  };
}

export function podDriverNameValidators(
  maxLength: number = POD_NAME_MAX_LENGTH
): ValidatorFn[] {
  return [
    podNameTrimmedLengthValidator(maxLength),
    Validators.pattern(POD_DRIVER_NAME_PATTERN),
  ];
}

export function podCarrierNameValidators(
  maxLength: number = POD_NAME_MAX_LENGTH
): ValidatorFn[] {
  return [Validators.required, podNameTrimmedLengthValidator(maxLength)];
}

export function podOptionalDateTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value?.dateTime) {
      return null;
    }
    const parsed = DateTime.fromISO(value.dateTime);
    if (!parsed.isValid) {
      return { invalidDateTime: true };
    }
    if (value.timeZoneId && +value.timeZoneId <= 0) {
      return { invalidTimeZone: true };
    }
    return null;
  };
}

export function resolvePodDateTimeForApi(
  dateTimeValue: { dateTime?: string; timeZoneId?: number } | null | undefined
): { dateTime: string; timeZoneId: number } {
  const storedTimeZoneId = +(localStorage.getItem('timezoneId') ?? '0');
  const defaultTimeZoneId = storedTimeZoneId > 0 ? storedTimeZoneId : 1;

  if (
    dateTimeValue?.dateTime &&
    DateTime.fromISO(dateTimeValue.dateTime).isValid
  ) {
    return {
      dateTime: dateTimeValue.dateTime,
      timeZoneId:
        dateTimeValue.timeZoneId && +dateTimeValue.timeZoneId > 0
          ? +dateTimeValue.timeZoneId
          : defaultTimeZoneId,
    };
  }

  return {
    dateTime: new Date().toISOString(),
    timeZoneId: defaultTimeZoneId,
  };
}

export function isPodCodeAndDescription(codeAndDescription: unknown): boolean {
  if (typeof codeAndDescription !== 'string') {
    return false;
  }
  const code = codeAndDescription.trim().split(/[\s-]/, 1)[0] ?? '';
  return code.toUpperCase() === POD_STATUS_CODE;
}

export function isPodStatusCodeRow(
  row:
    | {
        isPod?: boolean;
        statusCodeTypeName?: string;
        codeAndDescription?: string;
        name?: string;
      }
    | null
    | undefined
): boolean {
  if (!row) {
    return false;
  }
  if (row.isPod) {
    return true;
  }
  if (row.statusCodeTypeName === 'Delivery') {
    return true;
  }
  return isPodCodeAndDescription(row.codeAndDescription ?? row.name);
}

function parseRowExtra(
  row: Record<string, unknown>
): Record<string, unknown> | null {
  const extra = row['extra'];
  if (extra && typeof extra === 'object') {
    return extra as Record<string, unknown>;
  }
  if (typeof extra === 'string' && extra.trim()) {
    try {
      return JSON.parse(extra) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return null;
}

function resolvePodDateTimeFromRow(row: Record<string, unknown>): {
  dateTime?: string;
  timeZoneId?: number;
} {
  const dateTimeAndTimezone = row['dateTimeAndTimezone'];
  if (
    dateTimeAndTimezone &&
    typeof dateTimeAndTimezone === 'object' &&
    !Array.isArray(dateTimeAndTimezone)
  ) {
    const pickerValue = dateTimeAndTimezone as {
      dateTime?: unknown;
      timeZoneId?: unknown;
    };
    if (pickerValue.dateTime) {
      return {
        dateTime: String(pickerValue.dateTime),
        timeZoneId:
          pickerValue.timeZoneId != null && +pickerValue.timeZoneId > 0
            ? +pickerValue.timeZoneId
            : undefined,
      };
    }
  }

  const extraObj = parseRowExtra(row);
  const dateTime =
    row['dateTime'] ??
    row['date'] ??
    extraObj?.['dateTime'] ??
    extraObj?.['podDateTime'];
  const timeZoneId = row['timeZoneId'] ?? extraObj?.['timeZoneId'];

  return {
    dateTime: dateTime ? String(dateTime) : undefined,
    timeZoneId:
      timeZoneId != null && +timeZoneId > 0 ? +timeZoneId : undefined,
  };
}

export function resolvePodFieldsFromRow(
  row: Record<string, unknown> | null | undefined
): {
  driverName: string;
  carrierName: string;
  dateTime?: string;
  timeZoneId?: number;
} {
  if (!row) {
    return { driverName: '', carrierName: '' };
  }

  const extraObj = parseRowExtra(row);
  const dateTimeFields = resolvePodDateTimeFromRow(row);

  return {
    driverName: String(row['driverName'] ?? extraObj?.['driverName'] ?? '').trim(),
    carrierName: String(
      row['carrierName'] ?? extraObj?.['carrierName'] ?? ''
    ).trim(),
    dateTime: dateTimeFields.dateTime,
    timeZoneId: dateTimeFields.timeZoneId,
  };
}

export function resolvePodDateTimeForPopup(
  formDateTime: { dateTime?: string; timeZoneId?: number } | null | undefined,
  rowFields: { dateTime?: string; timeZoneId?: number }
): { dateTime?: string; timeZoneId?: number } {
  if (formDateTime?.dateTime) {
    return {
      dateTime: formDateTime.dateTime,
      timeZoneId: formDateTime.timeZoneId,
    };
  }

  return {
    dateTime: rowFields.dateTime,
    timeZoneId: rowFields.timeZoneId,
  };
}

export function isShipmentCbpHeld(value: unknown): boolean {
  return (
    value === true ||
    value === 'true' ||
    value === popupButtons.Yes ||
    value === 'Yes'
  );
}

export function isShipmentUserBlocked(value: unknown): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === HawbStatus.blocked;
  }
  return false;
}

function resolveHawbBlockedStatusFromInfo(
  data: Record<string, unknown> | null | undefined
): unknown {
  if (!data) {
    return undefined;
  }

  const hawbStatus = data['hawbStatus'];
  if (typeof hawbStatus === 'string') {
    return hawbStatus;
  }

  if (hawbStatus && typeof hawbStatus === 'object') {
    const currentStatus = (hawbStatus as Record<string, unknown>)[
      'currentStatus'
    ];
    if (currentStatus && typeof currentStatus === 'object') {
      return (currentStatus as Record<string, unknown>)['status'];
    }
  }

  return undefined;
}

/** Maps hawb info / summary row fields to POD held/blocked guard inputs. */
export function resolvePodGuardValuesFromHawbInfo(
  data: Record<string, unknown> | null | undefined,
  options?: {
    shipmentBlockStatus?: unknown;
    summaryRowFallback?: { cbpHeld?: unknown; isBlocked?: unknown } | null;
  }
): { cbpHeld: unknown; isBlocked: unknown } {
  const summaryRow = options?.summaryRowFallback;

  return {
    cbpHeld: data?.['cbpHeld'] ?? summaryRow?.cbpHeld,
    isBlocked:
      data?.['isBlocked'] ??
      summaryRow?.isBlocked ??
      resolveHawbBlockedStatusFromInfo(data) ??
      options?.shipmentBlockStatus,
  };
}

export function getSingleShipmentPodBlockedMessage(
  cbpHeld: unknown,
  isBlocked: unknown
): string | null {
  if (isShipmentCbpHeld(cbpHeld)) {
    return ModalPopupMessages.podHeldShipmentCannotDeliver;
  }
  if (isShipmentUserBlocked(isBlocked)) {
    return ModalPopupMessages.podBlockedShipmentCannotDeliver;
  }
  return null;
}

export function getBulkPodMasterBlockedMessage(
  masterRows: Array<{ cbpHeld?: unknown; isBlocked?: unknown }>
): string | null {
  if (!masterRows.length) {
    return null;
  }

  const heldCount = masterRows.filter(row =>
    isShipmentCbpHeld(row.cbpHeld)
  ).length;
  const blockedCount = masterRows.filter(row =>
    isShipmentUserBlocked(row.isBlocked)
  ).length;
  const ineligibleCount = masterRows.filter(
    row =>
      isShipmentCbpHeld(row.cbpHeld) || isShipmentUserBlocked(row.isBlocked)
  ).length;

  if (ineligibleCount < masterRows.length) {
    return null;
  }

  if (heldCount === masterRows.length) {
    return ModalPopupMessages.bulkPodAllHeld;
  }
  if (blockedCount === masterRows.length) {
    return ModalPopupMessages.bulkPodAllBlocked;
  }
  return ModalPopupMessages.bulkPodAllHeldOrBlocked;
}

export function mapStatusCodeAdditionalDetails(row: {
  description?: string;
  recipientName?: string;
  deliveryLocation?: string;
  isPod?: boolean;
  statusCodeTypeName?: string;
  codeAndDescription?: string;
  driverName?: string;
  carrierName?: string;
}): { isPod: boolean; additionalDetails: string } {
  const isPod = isPodStatusCodeRow(row);
  return {
    isPod,
    additionalDetails: isPod
      ? ModalPopupMessages.viewAdditionalDetails
      : getAdditionalDetails(
          row.description,
          row.recipientName,
          row.deliveryLocation
        ),
  };
}

export function getPodAdditionalDetailsDisplay(
  driverName?: string,
  carrierName?: string
): string {
  const parts = [carrierName?.trim(), driverName?.trim()].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Add POD Details';
}

/** Hyperlink text for a POD row: "View Additional Details" once names are entered, else the default label. */
export function getPodAdditionalDetailsText(
  driverName?: string,
  carrierName?: string
): string {
  const hasDetails = !!(driverName?.trim() || carrierName?.trim());
  return hasDetails
    ? ModalPopupMessages.viewAdditionalDetails
    : getPodAdditionalDetailsLabel();
}

export function getPodAdditionalDetailsLabel(): string {
  return ModalPopupMessages.additionalDetails;
}

export function isPodFormReady(form: FormGroup): boolean {
  if (!form) {
    return false;
  }
  const carrier = (form.get('carrierName')?.value ?? '').toString().trim();
  if (!carrier) {
    return false;
  }
  form.updateValueAndValidity({ emitEvent: false });
  return !form.invalid;
}

export function getPodNameRequiredError(): string {
  return ModalPopupMessages.podCarrierNameRequiredField;
}

export function getPodFieldErrorMessage(
  form: FormGroup,
  controlName: string,
  errorMessages: Record<string, string>
): string {
  if (
    form.hasError('podNameRequired') &&
    controlName === 'carrierName' &&
    form.get('carrierName')?.touched
  ) {
    return getPodNameRequiredError();
  }

  const control = form.get(controlName);
  if (!control?.errors) {
    return '';
  }

  if (control.errors['required']) {
    return errorMessages[controlName] ?? '';
  }
  if (control.errors['invalidDateTime'] || control.errors['invalidTimeZone']) {
    return errorMessages['invalidDateTime'] ?? '';
  }
  if (control.errors['pattern']) {
    return ModalPopupMessages.podDriverNameInvalid;
  }
  if (control.errors['minlength']) {
    return ModalPopupMessages.podNameMinLength;
  }
  if (control.errors['maxlength']) {
    return ModalPopupMessages.podNameMaxLength(
      control.errors['maxlength'].requiredLength
    );
  }

  return errorMessages[controlName] ?? '';
}

export function checkPodControlHasError(
  form: FormGroup,
  controlName: string,
  commonServiceCheck: (form: FormGroup, controlName: string) => boolean
): boolean {
  if (
    form.hasError('podNameRequired') &&
    controlName === 'carrierName' &&
    form.get('carrierName')?.touched
  ) {
    return true;
  }
  return commonServiceCheck(form, controlName);
}
